import { useQuery } from '@tanstack/react-query';
import type { DeviceConfigurationApi } from '@model/DeviceConfiguration';
import { getConfiguration } from '@services/configurationManager';
import type {
  ConfigurationsListResponse,
  ResearchConfigurationsLiteResponse,
} from '@types';
import { configurationQueryKey, userConfigurationsQueryKey } from './keys';

export { configurationQueryKey, userConfigurationsQueryKey } from './keys';

const API_BASE_URL = import.meta.env.VITE_API_URL;

/** Legacy envelope before list endpoint returned paginated `items`. */
type LegacyUserConfigurationsResponse = {
  data?: DeviceConfigurationApi[];
};

const toDeviceConfigurationApi = (
  item: Record<string, unknown>,
  userId: string
): DeviceConfigurationApi =>
  ({
    baseline: Number(item.baseline ?? 0),
    config: {},
    created_at: String(item.created_at ?? ''),
    device_id: String(item.device_id ?? ''),
    hrData: [],
    hrData_repaired: null,
    id: Number(item.id ?? 0),
    patient_id: Number(item.patient_id ?? 0),
    results: [],
    scenario: String(item.scenario ?? ''),
    status: item.status as DeviceConfigurationApi['status'],
    type: String(item.type ?? ''),
    updated_at: item.updated_at == null ? '' : String(item.updated_at),
    user_id: userId,
    ended_at: item.ended_at == null ? null : String(item.ended_at),
    sensor_id: item.sensor_id == null ? null : Number(item.sensor_id),
    started_at: item.started_at == null ? null : String(item.started_at),
    patientCode: item.patientCode == null ? '' : String(item.patientCode),
    deviceName: item.deviceName == null ? '' : String(item.deviceName),
    deviceCode: item.deviceCode == null ? '' : String(item.deviceCode),
    connectedDevices: Array.isArray(item.connectedDevices)
      ? (item.connectedDevices as DeviceConfigurationApi['connectedDevices'])
      : [],
    videoId: item.videoId == null ? null : Number(item.videoId),
    baselineDuration:
      item.baselineDuration == null ? null : Number(item.baselineDuration),
    hasHrDataRepaired: Boolean(item.hasHrDataRepaired),
  }) as DeviceConfigurationApi;

const fetchUserConfigurations = async (
  userId: string
): Promise<DeviceConfigurationApi[]> => {
  const response = await fetch(
    `${API_BASE_URL}/services/v1/users/${userId}/configurations-lite`,
    {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const result = (await response.json()) as
    | ConfigurationsListResponse
    | ResearchConfigurationsLiteResponse
    | LegacyUserConfigurationsResponse;

  if ('items' in result && Array.isArray(result.items)) {
    return result.items.map(item =>
      toDeviceConfigurationApi(
        {
          ...item,
          user_id: userId,
        },
        userId
      )
    );
  }

  const legacy = result as LegacyUserConfigurationsResponse;
  return legacy.data ?? [];
};

const fetchConfiguration = async (
  configurationId: string
): Promise<DeviceConfigurationApi> => {
  const { data } = await getConfiguration(configurationId);
  return data;
};

export const useUserConfigurationsQuery = (userId?: string) => {
  return useQuery({
    queryKey: userConfigurationsQueryKey(userId),
    queryFn: () => fetchUserConfigurations(userId!),
    enabled: Boolean(userId),
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};

export const useConfigurationQuery = (configurationId?: string) => {
  return useQuery({
    queryKey: configurationQueryKey(configurationId),
    queryFn: () => fetchConfiguration(configurationId!),
    enabled: Boolean(configurationId),
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: false,
  });
};
